import { DemoDeviceSerials, DeviceTypes, InstallationType } from '@store';

import {
  Address,
  AggregatedStatus,
  DeviceRoles,
  DeviceStatusType,
  GatewayType,
  GetResponse,
  Installation,
  OwnershipType,
  SerialEditorType,
  TargetRealmType,
  UserRoleType,
} from '../../models';
import { getRandomMock, mockedAddresses, mockedNames } from '../demo-constants';
import { Salutation } from '../users/users.model';
import {
  AccessList,
  InstallationSummary,
  MaterialDetailsResponse,
  GatewayMaterialDetailsResponse,
} from './equipment.model';

export class MockedAccessList {
  constructor(public id: number) {}

  mock(): GetResponse<AccessList> {
    const [name, surname] = getRandomMock<string>(mockedNames).split(' ');
    const { street, houseNumber, zip, city } = getRandomMock<Address>(mockedAddresses);
    return {
      data: {
        resourceId: 9037,
        owner: {
          id: this.id,
          viAccount: '63c4e3dd-52c3-4b12-a24d-9f7f09289773',
          salutation: Salutation.Mr,
          salutationV2: 'Mr',
          firstName: name,
          lastName: surname,
          email: 'vicaredev+wallbox@viessmann-climatesolutions.com',
          emailVerified: true,
          locale: 'en-us',
          address: {
            street,
            houseNumber,
            zip,
            city,
            region: null,
            country: 'pl',
            phoneNumber: null,
            faxNumber: null,
            geolocation: {
              latitude: 51.0602667,
              longitude: 17.0822521,
              timeZone: 'Europe/Warsaw',
            },
          },
          mobile: null,
          phoneNumber: null,
          faxNumber: null,
          role: UserRoleType.endUser,
          group: null,
          businessPartnerId: null,
          isDeleted: false,
          createdAt: '2022-07-27T10:24:11.484Z',
          editedAt: '2023-01-16T11:36:39.369Z',
          privacyPolicyAcceptedAt: null,
          privacyPolicyRequester: null,
        },
        maintainer: null,
        familyMembers: [],
        supporters: [],
        buildingManager: null,
        operator: null,
        serviceContractor: null,
        commercialOwner: null,
        partners: null,
        consumers: null,
      },
    };
  }
}

export class MockedInstallation {
  mock(): GetResponse<Installation> {
    return {
      data: {
        id: 10409,
        description: 'Wrocław',
        address: {
          street: 'Duńska',
          houseNumber: '9',
          zip: '54-427',
          city: 'Wrocław',
          region: null,
          country: 'PL',
          phoneNumber: null,
          faxNumber: null,
          geolocation: {
            latitude: 51.1103451,
            longitude: 16.9736671,
            timeZone: 'Europe/Warsaw',
          },
        },
        gateways: [
          {
            serial: '7736172044258222',
            version: '507.2213.4.0',
            firmwareUpdateFailureCounter: 0,
            autoUpdate: false,
            createdAt: '2022-11-23T10:46:34.600Z',
            producedAt: '2022-11-23T09:46:00.000Z',
            lastStatusChangedAt: '2023-01-26T07:28:39.677Z',
            aggregatedStatus: AggregatedStatus.Error,
            targetRealm: TargetRealmType.DC,
            roles: [],
            devices: [
              {
                gatewaySerial: '7736172044258222',
                id: '0',
                boilerSerial: '7722780280220220',
                boilerSerialEditor: SerialEditorType.User,
                bmuSerial: null,
                bmuSerialEditor: null,
                createdAt: '2022-11-23T10:47:16.139Z',
                editedAt: '2023-01-13T00:15:08.186Z',
                modelId: 'E3_Vitodens_200_NA_0821',
                status: DeviceStatusType.Online,
                deviceType: DeviceTypes.Heating,
                roles: [DeviceRoles.Heatpump],
                isBoilerSerialEditable: true,
              },
            ],
            gatewayType: GatewayType.Lancard,
            installationId: 10409,
            registeredAt: '2022-11-23T12:11:58.137Z',
            description: null,
            otaOngoing: false,
          },
        ],
        registeredAt: '2022-11-23T12:11:58.094Z',
        updatedAt: '2022-11-23T12:11:58.137Z',
        aggregatedStatus: AggregatedStatus.WorksProperly,
        servicedBy: null,
        heatingType: null,
        ownedByMaintainer: false,
        endUserWlanCommissioned: true,
        withoutViCareUser: false,
        installationType: InstallationType.Residential,
        buildingName: null,
        buildingEmail: null,
        buildingPhone: null,
        ownershipType: OwnershipType.ResidentialEndUser,
      },
    };
  }
}

export class MockMaterialGatewayNameResponse {
  mock(gatewaySerial: string): GatewayMaterialDetailsResponse {
    const materialGatewayNameMock = {
      materialDetails: [
        {
          material: gatewaySerial.slice(0, 7),
          name: '',
          description: 'RF-Modul WiFi / LAN ready',
        },
      ],
    };

    return materialGatewayNameMock;
  }
}

export class MockMaterialDetailsResponse {
  mock(boilerSerial: string): MaterialDetailsResponse {
    const materialDetailsMock = {
      materialDetails: [
        { name: '', material: boilerSerial.slice(0, 7), description: '', productGroup: '' },
      ],
    };

    switch (boilerSerial) {
      case DemoDeviceSerials.Vitocal252a:
      case DemoDeviceSerials.Vitocal252MPK:
        materialDetailsMock.materialDetails[0].name = 'Vitocal 252-A';
        materialDetailsMock.materialDetails[0].description = 'IDU VC 252-A AWOT-E-AC 251.A16 2C';

        return materialDetailsMock;

      case DemoDeviceSerials.Vitocal250A:
        materialDetailsMock.materialDetails[0].name = 'Vitocal 250-A';
        materialDetailsMock.materialDetails[0].description =
          'Indoor Vitocal 250-A AWO-E-AC 251.A16';

        return materialDetailsMock;

      case DemoDeviceSerials.Vitocal200:
      case DemoDeviceSerials.Vitocal200Offline:
      case DemoDeviceSerials.Vitocal200Error:
        materialDetailsMock.materialDetails[0].name = 'Vitocal 200-S';
        materialDetailsMock.materialDetails[0].description =
          'Inneneinh. Vitocal 200-S AWB-E 201.D08';

        return materialDetailsMock;

      case DemoDeviceSerials.Vitocal222:
        materialDetailsMock.materialDetails[0].name = 'Vitocal 222-S';
        materialDetailsMock.materialDetails[0].description =
          'In.einh. Vitocal 222-S AWBT-E-AC 221.C16';

        return materialDetailsMock;

      case DemoDeviceSerials.VitochargeV2Lag:
      case DemoDeviceSerials.Vitocharge:
      case DemoDeviceSerials.VitochargeVX3:
        materialDetailsMock.materialDetails[0].name = 'Vitocharge VX3';
        materialDetailsMock.materialDetails[0].description =
          'Wechselrichter Vitocharge VX3 Typ 4.6A';

        return materialDetailsMock;

      case DemoDeviceSerials.ViAir300:
      case DemoDeviceSerials.VitoairFS:
      case DemoDeviceSerials.ViAir300F:
        materialDetailsMock.materialDetails[0].name = 'Vitoair FS';
        materialDetailsMock.materialDetails[0].description = 'Vitoair FS 300 E';

        return materialDetailsMock;

      case DemoDeviceSerials.Vitovalor:
      case DemoDeviceSerials.VitovalorError:
        materialDetailsMock.materialDetails[0].name = 'Vitovalor PT2';
        materialDetailsMock.materialDetails[0].description = 'Vitovalor PT2 E19T';

        return materialDetailsMock;

      case DemoDeviceSerials.Vitodens343F:
        materialDetailsMock.materialDetails[0].name = 'Vitodens 343-F';
        materialDetailsMock.materialDetails[0].description = 'Vitodens 343-F B3UG-19 1,9-19kW';

        return materialDetailsMock;

      case DemoDeviceSerials.Vitodens100opentherm:
        materialDetailsMock.materialDetails[0].name = 'Vitodens 100-W';
        materialDetailsMock.materialDetails[0].description = 'Vitodens 100-W B1HF-25 3,2-25kW';

        return materialDetailsMock;

      case DemoDeviceSerials.Vitodens200WMaintenance:
      case DemoDeviceSerials.Vitodens200W:
        materialDetailsMock.materialDetails[0].name = 'Vitodens 200-W';
        materialDetailsMock.materialDetails[0].description = 'Vitodens 200-W B2HF-32 2,5-32kW';

        return materialDetailsMock;

      case DemoDeviceSerials.Vitodens300W:
      case DemoDeviceSerials.Vitodens300:
        materialDetailsMock.materialDetails[0].name = 'Vitodens 300-W';
        materialDetailsMock.materialDetails[0].description = 'Vitodens 300-W B3HG-19 1,9-19kW';

        return materialDetailsMock;

      case DemoDeviceSerials.Vitoligno300C:
        materialDetailsMock.materialDetails[0].name = 'Vitoligno 300-C';
        materialDetailsMock.materialDetails[0].description = 'Boiler Vitoligno 300-C VL3C 24kW';

        return materialDetailsMock;

      default:
        return materialDetailsMock;
    }
  }
}

export class MockInstallationSummary {
  mock(boilerSerial: string): InstallationSummary {
    const installationSummaryMock = {
      name: '',
      materialName: '',
      materialImage: '',
      equipmentNumber: '',
    };

    switch (boilerSerial) {
      case DemoDeviceSerials.Vitocal252a:
      case DemoDeviceSerials.Vitocal252MPK:
        installationSummaryMock.name = 'Vitocal 252-A';
        installationSummaryMock.materialImage =
          'https://static.viessmann-climatesolutions.com/resources/product_media/4613884VAN01001_1.PNG';

        return installationSummaryMock;

      case DemoDeviceSerials.Vitocal250A:
        installationSummaryMock.name = 'Vitocal 250-A';
        installationSummaryMock.materialImage =
          'https://static.viessmann-climatesolutions.com/resources/product_media/4613882VAN01001_1.PNG';

        return installationSummaryMock;

      case DemoDeviceSerials.Vitocal200:
      case DemoDeviceSerials.Vitocal200Offline:
      case DemoDeviceSerials.Vitocal200Error:
        installationSummaryMock.name = 'Vitocal 200-S';
        installationSummaryMock.materialImage =
          'https://static.viessmann-climatesolutions.com/resources/product_media/4611996VAN01001_1.PNG';

        return installationSummaryMock;

      case DemoDeviceSerials.Vitocal222:
        installationSummaryMock.name = 'Vitocal 222-S';
        installationSummaryMock.materialImage =
          'https://static.viessmann-climatesolutions.com/resources/product_media/4613888VAN01001_1.PNG';

        return installationSummaryMock;

      case DemoDeviceSerials.VitochargeV2Lag:
      case DemoDeviceSerials.Vitocharge:
      case DemoDeviceSerials.VitochargeVX3:
        installationSummaryMock.name = 'Vitocharge VX3';
        installationSummaryMock.materialImage =
          'https://static.viessmann-climatesolutions.com/resources/product_media/4613422VAN01001_1.PNG';

        return installationSummaryMock;

      case DemoDeviceSerials.ViAir300:
      case DemoDeviceSerials.VitoairFS:
      case DemoDeviceSerials.ViAir300F:
        installationSummaryMock.name = 'Vitoair FS';
        installationSummaryMock.materialImage =
          'https://static.viessmann-climatesolutions.com/resources/product_media/4614679VAN01001_1.PNG';

        return installationSummaryMock;

      case DemoDeviceSerials.Vitovalor:
      case DemoDeviceSerials.VitovalorError:
        installationSummaryMock.name = 'Vitovalor PT2';
        installationSummaryMock.materialImage =
          'https://static.viessmann-climatesolutions.com/resources/product_media/4612852VAN01001_1.PNG';

        return installationSummaryMock;

      case DemoDeviceSerials.Vitodens343F:
        installationSummaryMock.name = 'Vitodens 343-F';
        installationSummaryMock.materialImage =
          'https://static.viessmann-climatesolutions.com/resources/product_media/4612527VAN01001_1.PNG';

        return installationSummaryMock;

      case DemoDeviceSerials.Vitodens100opentherm:
        installationSummaryMock.name = 'Vitodens 100-W';
        installationSummaryMock.materialImage =
          'https://static.viessmann-climatesolutions.com/resources/product_media/4613432VAN01001_1.PNG';

        return installationSummaryMock;

      case DemoDeviceSerials.Vitodens200WMaintenance:
      case DemoDeviceSerials.Vitodens200W:
        installationSummaryMock.name = 'Vitodens 200-W';
        installationSummaryMock.materialImage =
          'https://static.viessmann-climatesolutions.com/resources/product_media/4612958VAN01001_1.PNG';

        return installationSummaryMock;

      case DemoDeviceSerials.Vitodens300W:
      case DemoDeviceSerials.Vitodens300:
        installationSummaryMock.name = 'Vitodens 300-W';
        installationSummaryMock.materialImage =
          'https://static.viessmann-climatesolutions.com/resources/product_media/4612416VAN01001_1.PNG';

        return installationSummaryMock;

      case DemoDeviceSerials.Vitoligno300C:
        installationSummaryMock.name = 'Vitoligno 300-C';
        installationSummaryMock.materialImage =
          'https://static.viessmann-climatesolutions.com/resources/product_media/4611478VAN01001_1.PNG';

        return installationSummaryMock;

      default:
        return installationSummaryMock;
    }
  }
}
